import { Controller, useFormContext, useWatch } from "react-hook-form";
import "./TicketSlaReminderConfig.scss";
import Textarea from "../../../../Forms/Controls/Textarea/Textarea";
import {
  FieldViewBlock,
  REQUIRED_ERROR_MESSAGE,
  TextInput,
} from "@SeemplicityDev/shared";
import { AutomationWizardStepProps } from "../../../AutomationsUtils";
import { useMemo } from "react";

const TicketSlaReminderConfig = ({
  isSummary,
}: {
  isSummary: AutomationWizardStepProps["isSummary"];
}) => {
  const methods = useFormContext();
  const { control } = methods;

  const daysBeforeSlaToRemind = useWatch({
    control,
    name: "daysBeforeSlaToRemind",
  });

  const slaReminderDaysInterval = useWatch({
    control,
    name: "slaReminderDaysInterval",
  });

  const slaReminderMaxTimes = useWatch({
    control,
    name: "slaReminderMaxTimes",
  });

  const configSummary = useMemo(() => {
    if (
      !!daysBeforeSlaToRemind &&
      !!slaReminderDaysInterval &&
      !!slaReminderMaxTimes
    ) {
      return `Send a reminder starting ${daysBeforeSlaToRemind} days before the SLA,
            then every ${slaReminderDaysInterval} days until ${slaReminderMaxTimes} reminders have been sent or the ticket is closed.`;
    }
  }, [daysBeforeSlaToRemind, slaReminderDaysInterval, slaReminderMaxTimes]);

  return !isSummary ? (
    <>
      <div className="configuration">
        <div className={"section-wrapper"}>
          <span className={"parameter-label"}>Start sending</span>
          <Controller
            name={`daysBeforeSlaToRemind`}
            shouldUnregister={true}
            defaultValue={10}
            render={({
              field: { value, onChange, onBlur },
              fieldState: { error },
            }) => {
              return (
                <TextInput
                  data-testid={"daysBeforeSlaToRemind"}
                  wrapperClassName={"days-input"}
                  onChange={onChange}
                  name={"daysBeforeSlaToRemind"}
                  onBlur={onBlur}
                  value={value}
                  error={error}
                  min={1}
                  type={"number"}
                  placeholder={"1-30"}
                  noMargin
                />
              );
            }}
            control={control}
            rules={{
              required: REQUIRED_ERROR_MESSAGE,
              pattern: {
                value: /^[1-9][0-9]*$/,
                message: "Invalid number of days",
              },
              max: {
                value: 30,
                message: "Number of days cannot be greater than 30.",
              },
            }}
          />
          days before SLA
        </div>
        <div className="section-wrapper">
          <span className={"parameter-label"}>Repeat every</span>
          <Controller
            name={`slaReminderDaysInterval`}
            shouldUnregister={true}
            defaultValue={2}
            render={({
              field: { value, onChange, onBlur },
              fieldState: { error },
            }) => {
              return (
                <TextInput
                  data-testid={"slaReminderDaysInterval"}
                  wrapperClassName={"days-input"}
                  onChange={onChange}
                  name={"slaReminderDaysInterval"}
                  onBlur={onBlur}
                  value={value}
                  error={error}
                  min={1}
                  type={"number"}
                  placeholder={"1-7"}
                  noMargin
                />
              );
            }}
            control={control}
            rules={{
              required: REQUIRED_ERROR_MESSAGE,
              pattern: {
                value: /^[1-7]*$/,
                message: "Invalid number of days",
              },
              max: {
                value: 20,
                message: "Number of days cannot be greater than 7.",
              },
            }}
          />
          day(s)
        </div>
        <div className="section-wrapper">
          <span className={"parameter-label"}>Stops after</span>
          <Controller
            name={`slaReminderMaxTimes`}
            shouldUnregister={true}
            defaultValue={10}
            render={({
              field: { value, onChange, onBlur },
              fieldState: { error },
            }) => {
              return (
                <TextInput
                  data-testid={"slaReminderMaxTimes"}
                  wrapperClassName={"days-input"}
                  onChange={onChange}
                  name={"slaReminderMaxTimes"}
                  onBlur={onBlur}
                  value={value}
                  error={error}
                  min={1}
                  type={"number"}
                  placeholder={"1-20"}
                  noMargin
                />
              );
            }}
            control={control}
            rules={{
              required: REQUIRED_ERROR_MESSAGE,
              pattern: {
                value: /^[1-9][0-9]*$/,
                message: "Invalid number of days",
              },
              max: {
                value: 20,
                message: "Number of days cannot be greater than 20.",
              },
            }}
          />
          reminders or ticket is closed
        </div>
      </div>
      <div className="ticket-comment-section">
        <div className="ticket-comment-title">Ticket Comment</div>
        <div className="ticket-comment-description">
          When reminders are triggered, a ticket comment will be posted
        </div>
        <div className="ticket-comment-title">
          If SLA is still valid (on time):
        </div>
        <Textarea
          wrapperClassName={"ticket-comment-textarea"}
          value={
            "@{ticket assignee} Reminder: This ticket’s SLA will expire in {number of days} days."
          }
          disabled
          size="custom"
          rowNumber={2}
        />
        <div className="ticket-comment-title">If SLA is past due:</div>
        <Textarea
          wrapperClassName={"ticket-comment-textarea"}
          size="custom"
          value={
            "@{ticket assignee} This ticket is now overdue — the SLA was missed {number of days} days ago."
          }
          disabled
          rowNumber={2}
        />
      </div>
    </>
  ) : (
    <FieldViewBlock label="Ticket SLA Reminders" text={configSummary} />
  );
};

export default TicketSlaReminderConfig;
